import { useEffect, useId, useState, type ReactNode } from 'react'
import { NavLink } from 'react-router'
import { Badge } from '../badge/Badge'
import { Tooltip } from '../tooltip/Tooltip'
import type { SidebarBadge, SidebarItemDef } from './types'
import styles from './Sidebar.module.css'

export interface SidebarItemProps {
  def: SidebarItemDef
  onItemClick?: (item: SidebarItemDef) => void
  /**
   * Rail mode. The label is visually hidden at 56px, so it comes back as a
   * hover/focus flyout beside the icon — without it the rail is a column of
   * unexplained glyphs to anyone who has not memorised them.
   */
  collapsed?: boolean
  /** localStorage key for whether `def.children` are expanded. */
  storageKey?: string
  /** Shows the children regardless of the user's toggle (used while searching). */
  forceExpanded?: boolean
  /** Rendered in a parent item's sub-list. */
  nested?: boolean
}

const STORAGE_PREFIX = 'nexus-sidebar-item:'

function renderBadge(badge: SidebarBadge): ReactNode {
  const variant = badge.variant ?? 'default'
  if (badge.kind === 'dot') {
    return <Badge variant={variant} size="xs" className={styles.badgeDot} aria-hidden="true" />
  }
  if (badge.kind === 'count') {
    return <Badge variant={variant} size="xs">{badge.value}</Badge>
  }
  return <Badge variant={variant} size="xs">{badge.value}</Badge>
}

export function SidebarItem({ def, onItemClick, collapsed, storageKey, forceExpanded, nested }: SidebarItemProps) {
  const children = def.children ?? []
  if (children.length > 0 && !collapsed) {
    return (
      <SidebarParentItem
        def={def}
        items={children}
        onItemClick={onItemClick}
        storageKey={storageKey}
        forceExpanded={forceExpanded}
      />
    )
  }
  return <SidebarLeafItem def={def} onItemClick={onItemClick} collapsed={collapsed} nested={nested} />
}

function SidebarParentItem({ def, items, onItemClick, storageKey, forceExpanded }: Omit<SidebarItemProps, 'collapsed' | 'nested'> & { items: SidebarItemDef[] }) {
  const listId = useId()
  const [userExpanded, setUserExpanded] = useState(() => {
    if (storageKey && typeof window !== 'undefined') {
      const stored = window.localStorage.getItem(STORAGE_PREFIX + storageKey)
      if (stored !== null) return stored === 'true'
    }
    return def.defaultExpanded ?? true
  })

  useEffect(() => {
    if (storageKey && typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_PREFIX + storageKey, String(userExpanded))
    }
  }, [userExpanded, storageKey])

  const expanded = forceExpanded || userExpanded

  return (
    <div className={styles.parent}>
      <div className={styles.parentRow}>
        <SidebarLeafItem def={def} onItemClick={onItemClick} />
        <button
          type="button"
          className={styles.parentToggle}
          onClick={() => setUserExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-controls={listId}
          aria-label={`${expanded ? 'Hide' : 'Show'} ${def.label}`}
        >
          <svg
            className={[styles.groupChevron, expanded && styles.groupChevronOpen].filter(Boolean).join(' ')}
            width="10"
            height="10"
            viewBox="0 0 12 12"
            fill="none"
            aria-hidden="true"
          >
            <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      <div id={listId} className={styles.subItems} role="group" aria-label={def.label} hidden={!expanded}>
        {items.map((child) => (
          <SidebarLeafItem key={child.id} def={child} onItemClick={onItemClick} nested />
        ))}
      </div>
    </div>
  )
}

function SidebarLeafItem({ def, onItemClick, collapsed, nested }: Pick<SidebarItemProps, 'def' | 'onItemClick' | 'collapsed' | 'nested'>) {
  const { label, icon, to, href, target, end, disabled, badge } = def

  const content = (
    <>
      {icon && <span className={styles.itemIcon}>{icon}</span>}
      <span className={styles.itemLabel}>{label}</span>
      {badge && <span className={styles.itemBadge}>{renderBadge(badge)}</span>}
    </>
  )

  const handleClick = () => onItemClick?.(def)

  let control: ReactNode

  if (disabled) {
    control = (
      <span className={[styles.item, nested && styles.subItem, styles.itemDisabled].filter(Boolean).join(' ')}>
        {content}
      </span>
    )
  } else if (href) {
    control = (
      <a
        href={href}
        target={target}
        rel={target === '_blank' ? 'noopener noreferrer' : undefined}
        onClick={handleClick}
        className={[styles.item, nested && styles.subItem].filter(Boolean).join(' ')}
      >
        {content}
      </a>
    )
  } else {
    control = (
      <NavLink
        to={to ?? '/'}
        end={end}
        onClick={handleClick}
        className={({ isActive }) => [styles.item, nested && styles.subItem, isActive && styles.itemActive].filter(Boolean).join(' ')}
      >
        {content}
      </NavLink>
    )
  }

  if (!collapsed) return control

  return (
    <Tooltip content={label} placement="right" className={styles.itemTooltip}>
      {control}
    </Tooltip>
  )
}
